import React, { useEffect, useMemo, useState } from 'react';

import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    TextField,
    type SelectChangeEvent
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import OverrideControls from './OverrideControls';
import ProductFeedbackSettings from './ProductFeedbackSettings';
import RecordingSettings from './RecordingSettings';
import {
    ClassLevelOverrideOptions,
    InstructorTARestrictedAccess,
    ParticipationSortType
} from '../../../types/adminConfig';
import AdminAuthContainer from '../AdminAuthContainer';
import AdminSettingsContainer from '../AdminSettingsContainer';
import SaveButton from '../components/SaveButton';
import { StyledFormGroup } from '../styles';
import { H2, StyledFormGroupContainer } from '../styles/headings';
import { AdminControlCheckbox } from '../utils/AdminControlCheckbox';
import { showNonCollabSetting } from '../utils/showAdvancedSetting';
import VirtualBackgrounds from '../VirtualBackgrounds';
const userJoinGracePeriodMarks = [
    { value: 0, label: '0' },
    { value: 15, label: '15' },
    { value: 30, label: '30' },
    { value: 45, label: '45' },
    { value: 60, label: '60' }
];

const FeaturesSettings = () => {
    const {
        school,
        loadingSchool,
        adminSettings,
        setAdminSettings,
        showExperimentalSettings,
        isSavingSettingsData,
        handleSaveSettings,
        isSavingDisabled,
        setSavingDisabled
    } = useContainer(AdminSettingsContainer);
    const { isAdmin, isSuperAdmin } = useContainer(AdminAuthContainer);
    const { featureSettings, authSettings } = adminSettings;
    const { t } = useTranslation('admin_settings');

    const nonCollabSettingShown = useMemo(
        () => showNonCollabSetting({ school, loadingSchool, isSuperAdmin }),
        [school, loadingSchool, isSuperAdmin]
    );

    const isTeams = school?.isTeams;

    /**
     * Legacy features (one per column)
     */
    const handleFeatureSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        featureSettings[event.target.id] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    /**
     * Current features (in options column)
     */
    const handleUpdateOptionsCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        featureSettings.options[event.target.name] = event.target.checked;
        if (event.target.name === 'hasNextGenChat' && !event.target.checked) {
            featureSettings.options.hasNextGenChatActivityFeed = false;
        }
        setAdminSettings({ ...adminSettings });
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const validateEmail = (email: string): boolean => emailRegex.test(email);
    const [classInvitationEmailAddressOverride, setclassInvitationEmailAddressOverride] = useState(
        featureSettings.options.classInvitationEmailAddressOverride
    );

    const [isClassInvitationEmailAddressValid, setIsClassInvitationEmailAddressValid] = useState(true);

    useEffect(() => {
        setSavingDisabled(!isClassInvitationEmailAddressValid);
    }, [isClassInvitationEmailAddressValid, setSavingDisabled]);

    const handleClassInvitationEmailAddress = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isEmailValid = !event.target.value || validateEmail(event.target.value);
        setclassInvitationEmailAddressOverride(event.target.value);
        setIsClassInvitationEmailAddressValid(isEmailValid);
        setAdminSettings((prev) => {
            const updatedSettings = { ...prev };
            updatedSettings.featureSettings.options.classInvitationEmailAddressOverride = event.target.value;
            return updatedSettings;
        });
    };

    const handleAuthSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        authSettings[event.target.id] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateOverrides = (event: React.ChangeEvent<HTMLInputElement>) => {
        featureSettings.overrides[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    const handleSelectOptionsSettingsChange = (
        event: SelectChangeEvent<InstructorTARestrictedAccess | ParticipationSortType>
    ) => {
        featureSettings.options[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };
    const minutes = t('configuration.minutes');

    return (
        <>
            <H2>{t('configuration.title')}</H2>
            <StyledFormGroupContainer>
                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.classAssessments}
                                onChange={handleFeatureSettingsChange}
                                id="classAssessments"
                                name="classAssessments"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.assessments') as string}
                    />
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                checked={featureSettings.assignments}
                                onChange={handleFeatureSettingsChange}
                                id="assignments"
                                name="assignments"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.assignments') as string}
                    />
                    {nonCollabSettingShown && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    checked={featureSettings.chat}
                                    onChange={handleFeatureSettingsChange}
                                    id="chat"
                                    name="chat"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.chat') as string}
                        />
                    )}
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.classGradebook}
                                onChange={handleFeatureSettingsChange}
                                id="classGradebook"
                                name="classGradebook"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.gradebook') as string}
                    />
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.hasShareFiles}
                                onChange={handleFeatureSettingsChange}
                                id="hasShareFiles"
                                name="hasShareFiles"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.share-files') as string}
                    />
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                checked={featureSettings.lms}
                                onChange={handleFeatureSettingsChange}
                                id="lms"
                                name="lms"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.lms') as string}
                    />
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                checked={featureSettings.loseFocusTracking}
                                onChange={handleFeatureSettingsChange}
                                id="loseFocusTracking"
                                name="loseFocusTracking"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.loseFocusTracking') as string}
                    />
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                checked={featureSettings.surveys}
                                onChange={handleFeatureSettingsChange}
                                id="surveys"
                                name="surveys"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.surveys') as string}
                    />
                    {nonCollabSettingShown && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    checked={featureSettings.polling}
                                    onChange={handleFeatureSettingsChange}
                                    id="polling"
                                    name="polling"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.polling') as string}
                        />
                    )}

                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasNextGenPolls}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="hasNextGenPolls"
                                name="hasNextGenPolls"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.next-gen-polls') as string}
                    />

                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                checked={featureSettings.proctoring}
                                onChange={handleFeatureSettingsChange}
                                id="proctoring"
                                name="proctoring"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.proctoring') as string}
                    />
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.classDocument}
                                onChange={handleFeatureSettingsChange}
                                id="classDocument"
                                name="classDocument"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.syllabus') as string}
                    />
                    {nonCollabSettingShown && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.sendInviteEmails}
                                    onChange={handleFeatureSettingsChange}
                                    id="sendInviteEmails"
                                    name="sendInviteEmails"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.inviteEmails') as string}
                        />
                    )}
                    {featureSettings.sendInviteEmails && (
                        <StyledFormGroup>
                            <InputLabel htmlFor="classInvitationEmailAddressOverride">
                                {t('configuration.class-invitation-email-address-override')}
                            </InputLabel>
                            <TextField
                                value={classInvitationEmailAddressOverride}
                                onChange={handleClassInvitationEmailAddress}
                                id="classInvitationEmailAddressOverride"
                                name="classInvitationEmailAddressOverride"
                                error={!isClassInvitationEmailAddressValid}
                                helperText={
                                    isClassInvitationEmailAddressValid
                                        ? t('configuration.class-invitation-email-address-override-helper-text')
                                        : t('users.dialogs.error-valid-email-required')
                                }
                                size="small"
                            />
                        </StyledFormGroup>
                    )}

                    {nonCollabSettingShown && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.whiteboard}
                                    onChange={handleFeatureSettingsChange}
                                    id="whiteboard"
                                    name="whiteboard"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.whiteboard') as string}
                        />
                    )}
                    {nonCollabSettingShown && !isTeams && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={authSettings.allowPMI}
                                    onChange={handleAuthSettingsChange}
                                    id="allowPMI"
                                    name="allowPMI"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('auth.allowPMI') as string}
                        />
                    )}

                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasNextGenGoogleDrive}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="hasNextGenGoogleDrive"
                                name="hasNextGenGoogleDrive"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.hasNextGenGoogleDrive') as string}
                    />

                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasNextGenOneDrive}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="hasNextGenOneDrive"
                                name="hasNextGenOneDrive"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.hasNextGenOneDrive') as string}
                    />

                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasTabScreenshare}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="hasTabScreenshare"
                                name="hasTabScreenshare"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.hasTabScreenshare') as string}
                    />

                    {!isTeams && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.enableBreakoutRoomSecurity}
                                    onChange={handleFeatureSettingsChange}
                                    id="enableBreakoutRoomSecurity"
                                    name="enableBreakoutRoomSecurity"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.enable-breakout-room-security-v2') as string}
                        />
                    )}

                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.allowTabLocking}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="allowTabLocking"
                                name="allowTabLocking"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.allow-tab-locking') as string}
                        hint={isTeams ? undefined : t('configuration.allow-tab-locking-tooltip')}
                    />

                    {showExperimentalSettings && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.autoTranscriptions}
                                    onChange={handleFeatureSettingsChange}
                                    id="autoTranscriptions"
                                    name="autoTranscriptions"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.autoTranscriptions') as string}
                        />
                    )}

                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasAllBreakoutTranscripts}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="hasAllBreakoutTranscripts"
                                name="hasAllBreakoutTranscripts"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.hasAllBreakoutTranscripts')}
                    />
                    <FormGroup sx={{ mt: 1 }}>
                        <FormLabel id="hasEndUserSurvey-label" sx={{ color: 'inherit' }}>
                            {t('configuration.hasEndUserSurvey')}
                        </FormLabel>
                        <FormControl sx={{ maxWidth: 300, marginLeft: 0 }} size="small" variant="outlined">
                            <Select
                                labelId="hasEndUserSurvey-select-label"
                                id="hasEndUserSurvey"
                                name="hasEndUserSurvey"
                                value={featureSettings.options.hasEndUserSurvey}
                                onChange={handleSelectOptionsSettingsChange}
                            >
                                <MenuItem value={InstructorTARestrictedAccess.Everyone}>
                                    {t('configuration.InstructorTARestrictedAccess.Everyone')}
                                </MenuItem>
                                <MenuItem value={InstructorTARestrictedAccess.InstructorsTAsOnly}>
                                    {t('configuration.InstructorTARestrictedAccess.InstructorsTAsOnly')}
                                </MenuItem>
                                <MenuItem value={InstructorTARestrictedAccess.NoOne}>
                                    {t('configuration.InstructorTARestrictedAccess.NoOne')}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </FormGroup>

                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasEndUserPerformanceTest}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="hasEndUserPerformanceTest"
                                name="hasEndUserPerformanceTest"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.hasEndUserPerformanceTest') as string}
                    />

                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.allowCustomVirtualBackgroundUpload}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="allowCustomVirtualBackgroundUpload"
                                name="allowCustomVirtualBackgroundUpload"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.allowCustomVirtualBackgroundUpload') as string}
                    />

                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasInMeetingTechCheck}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="hasInMeetingTechCheck"
                                name="hasInMeetingTechCheck"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.enable-in-meeting-tech-check')}
                    />

                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasNextGenChat}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="hasNextGenChat"
                                name="hasNextGenChat"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.enable-next-gen-chat')}
                    />
                    {featureSettings.options.hasNextGenChat && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.hasNextGenChatActivityFeed}
                                    onChange={handleUpdateOptionsCheckboxChange}
                                    id="hasNextGenChatActivityFeed"
                                    name="hasNextGenChatActivityFeed"
                                    disabled={!isAdmin || !featureSettings.options.hasNextGenChat}
                                />
                            }
                            label={t('configuration.enable-next-gen-chat-activity-feed')}
                            hint={t('configuration.enable-next-gen-chat-activity-feed-hint')}
                        />
                    )}
                    <AdminControlCheckbox
                        key="hasWhiteboardBORType"
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasWhiteboardBORType}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="hasWhiteboardBORType"
                                name="hasWhiteboardBORType"
                            />
                        }
                        label={t('configuration.enable-whiteboard-bor-type')}
                    />
                    {false && (
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.options.hasNextGenLearningToolSidebar}
                                    onChange={handleUpdateOptionsCheckboxChange}
                                    id="hasNextGenLearningToolSidebar"
                                    name="hasNextGenLearningToolSidebar"
                                    disabled={!isAdmin}
                                />
                            }
                            label={t('configuration.enable-next-gen-learning-tool-sidebar')}
                        />
                    )}
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.hasSyncSeatingCharts}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="hasSyncSeatingCharts"
                                name="hasSyncSeatingCharts"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.enable-sync-seating-charts')}
                    />
                     <AdminControlCheckbox
                        key="showAdvancedFollowMeOptions"
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.showAdvancedFollowMeOptions}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="showAdvancedFollowMeOptions"
                                name="showAdvancedFollowMeOptions"
                            />
                        }
                        label={t('configuration.show-advanced-follow-me-options')}
                    />
                </FormGroup>
            </StyledFormGroupContainer>

            <FormGroup>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={featureSettings.options.hasPresentations}
                            onChange={handleUpdateOptionsCheckboxChange}
                            id="hasPresentations"
                            name="hasPresentations"
                        />
                    }
                    label={t('configuration.allow-presentations')}
                />
                <OverrideControls
                    overrideName="hasPresentations"
                    overrideValue={featureSettings.overrides?.hasPresentations ?? ClassLevelOverrideOptions.NoOne}
                    handleUpdateOverrides={handleUpdateOverrides}
                    labelText={t('whoCanOverride')}
                    tooltipText={t('whoCanOverrideTooltip')}
                />
            </FormGroup>

            {!isTeams && (
                <>
                    <ProductFeedbackSettings />
                </>
            )}

            <VirtualBackgrounds />

            <AdminControlCheckbox
                sx={{ mb: 3 }}
                control={
                    <Checkbox
                        color="primary"
                        checked={featureSettings.options.disableClassBackgrounds}
                        onChange={handleUpdateOptionsCheckboxChange}
                        id="disableClassBackgrounds"
                        name="disableClassBackgrounds"
                        disabled={!isAdmin}
                    />
                }
                label={t('configuration.disableClassBackgrounds') as string}
                hint={t('configuration.disableClassBackgroundsHint') as string}
            />

            <>
                <H2>{t('configuration.learner-engagement')}</H2>
                <StyledFormGroupContainer>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.enableLearnerEngagement}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="enableLearnerEngagement"
                                name="enableLearnerEngagement"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.enable-learner-engagement') as string}
                    />

                    <FormGroup sx={{ mt: 1 }}>
                        <FormLabel id="participationSort-label" sx={{ color: 'inherit' }}>
                            {t('configuration.participation-sort')}
                        </FormLabel>
                        <FormControl sx={{ maxWidth: 300, marginLeft: 0 }} size="small" variant="outlined">
                            <Select
                                labelId="participation-sort-select-label"
                                id="participationSort"
                                name="participationSort"
                                value={featureSettings.options.participationSort}
                                onChange={handleSelectOptionsSettingsChange}
                            >
                                <MenuItem value={ParticipationSortType.Engagement}>
                                    {t('configuration.participation-sort-types.engagement')}
                                </MenuItem>
                                <MenuItem value={ParticipationSortType.TalkTime}>
                                    {t('configuration.participation-sort-types.talk-time')}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </FormGroup>
                </StyledFormGroupContainer>
            </>

            <RecordingSettings />

            <StyledFormGroupContainer>
                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                checked={featureSettings.options.disableCameraMicrophoneWhenRecording}
                                onChange={handleUpdateOptionsCheckboxChange}
                                id="disableCameraMicrophoneWhenRecording"
                                name="disableCameraMicrophoneWhenRecording"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('configuration.disableCameraMicrophoneWhenRecording') as string}
                    />
                </FormGroup>
            </StyledFormGroupContainer>
            <H2>{t('configuration.join-options.title')}</H2>
            <StyledFormGroupContainer>
                <FormGroup>
                    <FormLabel id={`allow-stutent-join-prior-label`}>
                        {t('configuration.join-options.student')}
                    </FormLabel>
                    <Box sx={{ width: '25vw', paddingLeft: '10px' }}>
                        <Slider
                            defaultValue={featureSettings.options.allowStudentJoinPriorMinutes}
                            min={0}
                            max={60}
                            step={5}
                            valueLabelDisplay="auto"
                            marks={userJoinGracePeriodMarks}
                            onChange={(event, value) => {
                                featureSettings.options.allowStudentJoinPriorMinutes = value as number;
                                setAdminSettings({ ...adminSettings });
                            }}
                            getAriaLabel={(value) => `${value} ${t('configuration.minutes')}`}
                        />
                    </Box>
                    <OverrideControls
                        overrideName="allowStudentJoinPriorMinutes"
                        overrideValue={
                            featureSettings.overrides?.allowStudentJoinPriorMinutes ?? ClassLevelOverrideOptions.NoOne
                        }
                        handleUpdateOverrides={handleUpdateOverrides}
                        labelText={t('whoCanOverride')}
                        tooltipText={t('whoCanOverrideTooltip')}
                    />
                </FormGroup>
            </StyledFormGroupContainer>

            <SaveButton isSaving={isSavingSettingsData} disabled={isSavingDisabled} onClick={handleSaveSettings} />
        </>
    );
};

export default FeaturesSettings;
